import React, { type Dispatch, type SetStateAction, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from '@grebban/style-system-react';
import { SearchIcon } from 'assets/media/icons/SearchIcon';
import Button from 'components/Button';
import { useTranslation } from 'react-i18next';
import { above } from '@sportson/core-web/utils/mediaqueries';
import Text from 'components/Text';

const SearchFormWrapper = styled('form')`
    display: flex;
    flex: 1;
    align-items: center;
    position: relative;
    border-radius: 56px;
`;
const ClearButton = styled('button')`
    color: var(--color-neutrals-400-darker);
    text-decoration: underline;

    padding: 11px 8px;
`;

const SearchFormControls = styled('div')`
    display: none;

    ${above['tablet.md']} {
        display: flex;
        align-items: center;
        position: absolute;
        right: ${({ isLight }) => (isLight ? '1px' : '0')};
    }

    ${({ isActive }) =>
        isActive &&
        `
        background-color: var(--color-base-white);
        border-radius: 56px;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    `}
`;

const SearchButton = styled(Button)``;

const Input = styled('input')`
    font-family: 'Emeric';
    font-size: 16px;
    font-style: normal;
    font-weight: 450;
    line-height: 100%;
    padding-right: 200px;
    height: 42px;
    width: 100%;
    padding: 12px 12px 12px 36px;
    border-radius: 56px;
    border: 1px solid var(--color-neutrals-400);
    background-color: ${({ isLight }) => (isLight ? 'var(--color-base-white)' : 'var(--color-base-black)')};
    color: var(--color-neutrals-500);

    :hover {
        color: var(--color-neutrals-400-darker);
    }

    :focus {
        color: ${({ isLight }) => (isLight ? 'var(--color-neutrals-300)' : 'var(--color-neutrals-500)')};
    }

    &::placeholder {
        opacity: 1;
    }

    ${({ isActive, isLight }) =>
        isActive &&
        `
        :focus {
            color: ${isLight ? 'var(--color-base-black)' : 'var(--color-base-white)'};
        }
        border: 1px solid ${isLight ? 'var(--color-neutrals-500)' : 'var(--color-neutrals-300)'};
    `}

    &:focus,
    &:focus-visible {
        outline: none;
    }
`;

const IconWrapper = styled('div', {
    shouldForwardProp: (prop) => ['iconLeftPadding'].indexOf(prop) === -1,
})`
    position: absolute;
    width: fit-content;
    left: ${({ iconLeftPadding }) => iconLeftPadding};
`;

interface Props {
    isLight?: boolean;
    iconLeftPadding?: string;
    inputPlaceholder?: string;
    value?: string;
    marginLeft?: string;
    width?: string | (string | null)[];
    setValue: Dispatch<SetStateAction<string>>;
    hideSubmitButton?: boolean;
}

export const SearchInput = ({
    isLight = false,
    iconLeftPadding = '12px',
    inputPlaceholder,
    value,
    marginLeft,
    hideSubmitButton = false,
    setValue,
    ...rest
}: Props) => {
    const { t } = useTranslation();
    const [isActive, setIsActive] = useState(false);
    const [hasValue, setHasValue] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const placeholderText =
        inputPlaceholder ||
        useSelector(({ application }) => application.header.data.search?.inputPlaceholderText) ||
        '';

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (inputRef.current) {
            const searchTerm = inputRef.current.value.slice(0, 150);
            window.location.href = `/sok?search=${encodeURIComponent(searchTerm)}`;
        }
    };

    const clearSearch = () => {
        if (inputRef.current) {
            setHasValue(false);
            setValue('');
            inputRef.current.value = '';
        }
    };

    return (
        <SearchFormWrapper isActive={isActive} marginLeft={marginLeft} {...rest} onHover={() => setIsActive(true)}>
            <IconWrapper iconLeftPadding={iconLeftPadding}>
                <SearchIcon fill="var(--color-neutrals-400-darker)" width="16px" height="16px" />
            </IconWrapper>
            <Input
                ref={inputRef}
                isActive={isActive}
                isLight={isLight}
                type="search"
                placeholder={placeholderText}
                name="search"
                maxLength="150"
                onFocus={() => setIsActive(true)}
                onBlur={() => setIsActive(false)}
                onChange={(e) => {
                    setHasValue(e.target.value.length > 0);
                    setValue(e.target.value);
                }}
            />
            <SearchFormControls isActive={isActive} isLight={isLight}>
                {hasValue && (
                    <ClearButton type="button" onClick={() => clearSearch()}>
                        <Text typography="UI/16_100_0_450">{t('search.clear')}</Text>
                    </ClearButton>
                )}
                {!hideSubmitButton && (
                    <SearchButton type="submit" onClick={(e) => handleSubmit(e)}>
                        {t('search.search')}
                    </SearchButton>
                )}
            </SearchFormControls>
        </SearchFormWrapper>
    );
};
